import { Alert, Button, Card, Divider, SimpleGrid, Skeleton, Stack, Text } from "@mantine/core";
import { IconAlertTriangle, IconArrowRight, IconCoins, IconFileInvoice, IconFileText, IconReportMoney, IconUsersGroup } from "@tabler/icons-react";
import useSWR from "swr";
import { ChargeContractApi, TenantActivityApi, TenantSummaryApi } from "../../../../apis";
import type { ChargeContract } from "../../../../apis/chargeContract";
import type { ActivityItem } from "../../../../apis/tenantActivity";
import { ActivityFeed } from "../../../../components/billing/ActivityFeed";
import { EmptyState } from "../../../../components/billing/EmptyState";
import { MoneyCell } from "../../../../components/billing/MoneyCell";
import { SectionHeader } from "../../../../components/billing/SectionHeader";
import { StatCard } from "../../../../components/billing/StatCard";
import { formatDate, formatMoney } from "../../../../utils/format";

// ── Contract summary card ─────────────────────────────────────────────────────

function MetaField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>{label}</Text>
      <Text size="sm" fw={500} style={{ fontVariantNumeric: "tabular-nums" }}>{value}</Text>
    </Stack>
  );
}

function ActiveContractSummary({ contract, onOpen }: { contract: ChargeContract; onOpen?: () => void }) {
  const from = formatDate(contract.effectiveFrom);
  const until = contract.effectiveUntil ? formatDate(contract.effectiveUntil) : "одоог хүртэл";

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="md">
        <SectionHeader
          eyebrow="Гэрээ"
          title="Идэвхтэй гэрээ — тойм"
          action={onOpen && (
            <Button variant="subtle" color="brand" size="compact-sm" rightSection={<IconArrowRight size={16} />} onClick={onOpen}>
              Гэрээ бүрэн харах
            </Button>
          )}
        />
        <Divider />
        <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md">
          <MetaField label="Гэрээ №" value={contract.contractNumber} />
          <MetaField label="Нэр" value={contract.contractName} />
          <MetaField label="Валют" value={contract.currency} />
          <MetaField label="Хүчинтэй хугацаа" value={`${from} → ${until}`} />
          <MetaField label="Бүрэлдэхүүн" value={`${contract.components.length} бүрэлдэхүүн`} />
        </SimpleGrid>
      </Stack>
    </Card>
  );
}

// ── Recent activity timeline (UI-SPEC §2.2) ──────────────────────────────────

function RecentActivity({ tenantId }: { tenantId: string }) {
  const { data, error, isLoading, mutate } = useSWR(
    `swr.billing.tenant.activity.${tenantId}`,
    () => TenantActivityApi.activity(tenantId, { days: 30, limit: 20 })
  );

  const items: ActivityItem[] = Array.isArray(data) ? data : [];

  return (
    <ActivityFeed
      items={items}
      loading={isLoading}
      error={error}
      onRetry={() => mutate()}
      title="Сүүлийн идэвхжил (сүүлийн 30 хоног)"
      emptyText="Сүүлийн 30 хоногт идэвхжил алга."
    />
  );
}

// ── Summary tiles (UI-SPEC §2.2 — 4 financial tiles) ─────────────────────────

function SummaryTiles({ tenantId }: { tenantId: string }) {
  const { data: summary, error, isLoading, mutate } = useSWR(
    `swr.billing.tenant.summary.${tenantId}`,
    () => TenantSummaryApi.getSummary(tenantId)
  );

  if (error) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Тойм ачаалж чадсангүй">
        <Stack gap="xs" align="flex-start">
          <Text size="sm">Дахин оролдоно уу.</Text>
          <Button size="xs" variant="light" color="red" onClick={() => mutate()}>
            Дахин ачаалах
          </Button>
        </Stack>
      </Alert>
    );
  }

  if (isLoading || !summary) {
    return (
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} height={132} radius="md" />)}
      </SimpleGrid>
    );
  }

  const { thisMonthRevenue, outstandingInvoices, tenantFeesCollected, endCustomerCharges } = summary;

  return (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
      <StatCard
        icon={<IconReportMoney size={20} />}
        label="Энэ сарын орлого"
        // null is not zero — show a placeholder, never a money figure.
        value={thisMonthRevenue ? <MoneyCell value={thisMonthRevenue.total} /> : "—"}
        sub={thisMonthRevenue ? "Энэ сар" : "тооцоолол хийгдээгүй"}
      />
      <StatCard
        icon={<IconFileInvoice size={20} />}
        label="Төлөгдөөгүй нэхэмжлэх"
        value={String(outstandingInvoices.count)}
        sub={<MoneyCell value={outstandingInvoices.amountDue} />}
      />
      <StatCard
        icon={<IconCoins size={20} />}
        label="Цуглуулсан шимтгэл"
        value={<MoneyCell value={tenantFeesCollected.total} />}
        sub={`${tenantFeesCollected.count} бичлэг`}
      />
      <StatCard
        icon={<IconUsersGroup size={20} />}
        label="End-customer төлбөр"
        value={endCustomerCharges ? <MoneyCell value={endCustomerCharges.total} /> : "—"}
        sub={
          endCustomerCharges
            ? `${endCustomerCharges.merchantCount} × ${formatMoney(endCustomerCharges.perCustomerAmount)}`
            : "EC бүрэлдэхүүн алга"
        }
      />
    </SimpleGrid>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────────

export function TenantOverviewTab({ tenantId, onOpenContract }: { tenantId: string; onOpenContract?: () => void }) {
  const { data: contractData, isLoading: contractsLoading } = useSWR(
    `swr.billing.tenant.contracts.${tenantId}`,
    () => ChargeContractApi.list({ tenantId, page: 1, limit: 50 })
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const contractRows: ChargeContract[] = (contractData as any)?.rows ?? (Array.isArray(contractData) ? (contractData as ChargeContract[]) : []);
  const activeContract = contractRows.find((c) => c.status === "ACTIVE") ?? null;

  return (
    <Stack gap="md">
      {/* ── Financial summary tiles (§2.2) ── */}
      <SummaryTiles tenantId={tenantId} />

      {/* ── Active contract summary ── */}
      {!contractsLoading && activeContract && (
        <ActiveContractSummary contract={activeContract} onOpen={onOpenContract} />
      )}

      {!contractsLoading && !activeContract && (
        <Card withBorder padding="lg" radius="md">
          <Stack gap="md">
            <SectionHeader eyebrow="Гэрээ" title="Идэвхтэй гэрээ — тойм" />
            <Divider />
            <EmptyState icon={<IconFileText size={26} />} message="Одоогоор идэвхтэй гэрээ байхгүй байна." />
          </Stack>
        </Card>
      )}

      {/* ── Recent activity timeline ── */}
      <RecentActivity tenantId={tenantId} />
    </Stack>
  );
}
